import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnnotateLayer } from '~/deck15/annotate/AnnotateLayer';
import {
  addAnnotation,
  clearAnnotations,
  focusAnnotation,
  requestAnnotationEdit,
} from '~/deck15/annotate/annotateBus';
import { TextEditRing } from '~/deck15/edit/TextEditRing';
import { MorphCanvas } from '~/deck15/morph/MorphCanvas';
import { DeckMark } from '~/design-system/components/v1_5/Header/DeckMark';
import { PreviewPane, TextToolbar } from '~/design-system/components/v1_5/Preview';
import { cn } from '~/lib/utils';
import {
  METRICS_HEADLINE,
  METRICS_INK,
  METRICS_PLAIN_INK,
  METRICS_SERIF,
  SlideArt,
  type SlideHeadlineStyle,
} from './SlideArt';
import { useBeat, useInView, usePrefersReducedMotion } from './useDemoBeat';
import styles from './EditAndPresentDemo.module.css';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

/**
 * Feature row 3 — "Edit it, shape it, present it your way" (Beka, 2026-09-12).
 *
 * One artifact, three acts. The headline is selected and the real `TextToolbar` restyles it live;
 * the pen comes out and two real annotations land on it, in order; the notes are answered by a
 * rewrite; then everything that is not the artifact goes away and the artifact is presented.
 *
 * THE REWRITE HAS TO SHOW (Beka, 2026-09-16). It used to change two lines of copy on a slide that
 * already looked finished, and read as nothing happening. Now the first draft is the slide's plain
 * look — grey page, white cards, no colour — the second note asks for the brand, and what comes back
 * from the rewrite is the blue slide with its new headline: the change is the whole page.
 *
 * The parts are the product's, not drawings of it — `PreviewPane` with its own fit and chrome,
 * `TextEditRing` reading the frame's scale off `--hv15-art-scale`, `AnnotateLayer` rendering notes
 * off `annotateBus`, and the artifact itself is one of the eight `AgentAsksDemo` already has open.
 *
 * THE FINALE IS NOT A PRESENTER. It was one — a black letterbox over the plate with a counter and
 * arrows, rebuilt at panel scale because `BirtvyOpenPresent` is a fixed z-3000 overlay — and it read
 * as a second screen cutting in (Beka, 2026-09-12). Now the artifact you just edited is the thing
 * that presents: the editor's chrome leaves, and the same slide, at the same size, moves to the
 * centre of the plate. See `presentedRef`.
 */

const THEME = 'dark' as const;

/**
 * The demo's own key on the annotation bus.
 *
 * The bus is a module-global store keyed by slide, and this row is not a deck. A string of its own
 * cannot collide with an artifact id, and the run clears it on unmount so nothing outlives the page.
 */
const DEMO_SLIDE_KEY = 'landing-v2-present-demo';

/**
 * The run. Two generations bracket the edit — the artifact is written, worked on, and written again
 * once the notes have asked for something — because that is the loop the product actually is.
 *
 * There is no beat between selecting the headline and its toolbar arriving. There was — a `caret`
 * beat 800ms long before a `toolbar` one — and it read as the toolbar lagging the selection, which
 * is the one thing a selection toolbar must never do (Beka, 2026-09-12). `select` is both.
 */
const BEATS = [
  'generating',
  'resolving',
  'reading',
  'select',
  'restyle',
  'annotate',
  'note',
  'second',
  'secondNote',
  'regenerating',
  'apply',
  'present',
] as const;
type Beat = (typeof BEATS)[number];

const DWELL: Record<Beat, number> = {
  generating: 2200,
  resolving: 900,
  reading: 900,
  select: 1700,
  restyle: 2100,
  annotate: 1000,
  note: 2400,
  second: 900,
  secondNote: 2400,
  regenerating: 1700,
  apply: 1400,
  present: 4600,
};

/**
 * The headline before and after.
 *
 * Before is the first draft as the metrics slide authors it: the app's display face at 32, a label
 * where the slide wants a headline. After is the slide's own serif at 56 — the face its three figures
 * are already set in — so the restyle reads as the headline joining the slide rather than as a face
 * picked at random, and what the row presents is the reference Beka drew the slide to (2026-09-15),
 * with its headline a step larger than the reference's 48 at Beka's ask the same day. The toolbar
 * shows a size as it is set, stop or not. Family and size carry the restyle; the colour is the
 * look's ink, dark on the plain draft the toolbar edits and white once the brand note is answered.
 *
 * `family` is the NAME the toolbar shows; `stack` is what the headline is actually set in, and the
 * two are separate on purpose. The headline used to take a bare name, and a family list with no
 * generic fallback renders in the browser's default while a face is loading, which is Times — the
 * restyle swapped to the wrong face for a beat before it swapped to the right one (Beka, 2026-09-12).
 * Both stacks end in a generic family, and `useFacesReady` below fetches both cuts before the beat
 * that needs them, so there is nothing left for a fallback to fill.
 */
const BEFORE = { family: 'Inter Display', stack: 'var(--font-display)', weight: '500', size: 32 };
const AFTER = { family: 'Playfair Display', stack: METRICS_SERIF, weight: '400', size: 56 };

/**
 * What the notes ask for, and what the artifact says once they are answered.
 *
 * The slide's three cards already tell the story — nine steps down to three, completion from 32% to
 * 71% — and its first-draft headline does not: "What happened to signups after we launched" is a
 * title that recounts rather than concludes. So the first note asks for the result, and the rewrite
 * gives one a reader can repeat. "Six steps gone" is the cards' nine-to-three in words; "Signups
 * doubled" is their 32 becoming 71.
 *
 * The second is about the look, not the words: the draft is plain, and the note asks for the brand.
 * It is answered by the whole page — cobalt, white type, the serif figures and the lime October bar.
 * It replaced a note asking for the launch card's pace (Beka, 2026-09-16), whose answer was one line
 * of 17px copy, so the launch card keeps its first-draft line now: no note asks for another.
 *
 * The first is an AREA note roped around the headline; the second a point note on the middle card's
 * bar, whose composer `popupPlacement` flips above the marker because it sits low in the slide.
 */
const NOTE_HEADLINE = 'Open with the result, not the history.';
const NOTE_STYLE = 'Too plain. Use our brand colours.';
const APPLIED_HEADLINE = 'Six steps gone. Signups doubled.';

/**
 * Where the second note is put down, in artifact units: on the August bar, in the middle card. The
 * card spans 465 to 816 of the slide's width, and the bar stands in the lower half of the card from
 * the track's foot at 543, so 500 is on the bar at its 58% whatever the headline is doing.
 */
const STYLE_NOTE_POINT = { x: 640, y: 500 };

/** The artifact's own space. Every rect below is in these units. */
const NATURAL_W = 1280;
const NATURAL_H = 720;

/**
 * THE PRESENTED SLIDE KEEPS ITS SIZE. Presenting moves it; it does not shrink it.
 *
 * It used to settle at 76% of the plate, and on the plate's narrow width that took a quarter off it
 * — presenting read as the slide being put away rather than put up (Beka, 2026-09-12: "it just needs
 * to center"). Now it lands at exactly the width the editor drew it, and the move is pure centring.
 *
 * The one exception is room. Below the 1280 breakpoint the plate is 660 wide and the editor draws the
 * slide at 662 — already cropped by the plate — so there the slide comes down only as far as it must
 * to sit inside the plate with this inset either side. At full width it never scales at all.
 */
const PRESENT_INSET = 32;

/**
 * The width `PreviewPane` fits this artifact to inside the fixed 736x539 stage, which is the same at
 * every breakpoint. The hand-off measures the real value every time; this is only the finale's size
 * when there was no editor on screen to measure — off-screen and reduced-motion readers, who start
 * on the held last beat.
 */
const EDITOR_ART_W = 662;

/** Where the presented slide rests: the editor's width, capped by the room, centred in the plate. */
function presentPose(plateW: number, plateH: number, editorW: number) {
  const width = Math.min(
    editorW,
    plateW - PRESENT_INSET * 2,
    (plateH - PRESENT_INSET * 2) * (NATURAL_W / NATURAL_H),
  );
  const s = width / NATURAL_W;
  return { x: (plateW - width) / 2, y: (plateH - NATURAL_H * s) / 2, s };
}

/**
 * Moving across the screen, so ease-in-out and not the ease-out of an entrance. 560ms is the brand
 * kit row's pane slide, so the two rows share one length for the one large movement each makes.
 */
const PRESENT_MS = 560;
const PRESENT_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

/** `ArtifactFrame`'s `.slide` radius, in screen pixels. The presented copy keeps the same corner. */
const FRAME_RADIUS = 10;

/**
 * Fetch the two cuts the restyle swaps between, before it happens.
 *
 * `document.fonts.load` resolves a face for the given shorthand and text and fetches it if it is not
 * on the page, which is what the headline would otherwise do on the frame it changes. Passing the
 * real strings pulls the right unicode-range subset rather than whichever one covers a space.
 */
function useFacesReady() {
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return;
    const text = `${METRICS_HEADLINE} ${APPLIED_HEADLINE}`;
    void Promise.all([
      document.fonts.load(`${BEFORE.weight} ${BEFORE.size}px "${BEFORE.family}"`, text),
      document.fonts.load(`${AFTER.weight} ${AFTER.size}px "${AFTER.family}"`, text),
    ]).catch(() => undefined);
  }, []);
}

export interface EditAndPresentDemoProps {
  className?: string;
}

export function EditAndPresentDemo({ className }: EditAndPresentDemoProps) {
  const [hostRef, inView] = useInView<HTMLDivElement>();
  const reduceMotion = usePrefersReducedMotion();
  const beat = useBeat(BEATS, DWELL, inView && !reduceMotion);
  const at = (name: Beat) => BEATS.indexOf(beat) >= BEATS.indexOf(name);

  useFacesReady();

  const restyled = at('restyle');
  const applied = at('apply');
  /* The headline is selected — ring and toolbar together — until the pen comes out. */
  const editing = at('select') && !at('annotate');
  /*
   * The pen is down from the first note until the rewrite starts, and not one beat longer.
   *
   * They are the ask; the generation is the answer; the artifact that comes back is clean. The veil
   * outranks them anyway (`z-index: 20` against the layer's 10), so this is the story and not the
   * stacking — but a marker sitting under a rewrite it caused would be wrong either way.
   */
  const annotating = at('annotate') && !at('regenerating');
  const presenting = at('present');

  /*
   * The generation overlay, on the beats a generation is actually happening.
   *
   * `band` only — the shader's wait state, what the morph loader shows while the agent is writing.
   * NOT `resolve`, and not `MorphResolveStage`: the resolve converges on `toTexture`, a raster of
   * the finished render, and this row has no way to produce one. Played without it the shader
   * converges on nothing and the card goes blank and pale for about a second before the artifact
   * appears — the white plate Beka saw (2026-09-12). So the band hands off the honest way instead:
   * it keeps drawing, and the veil over it fades out onto the artifact underneath.
   *
   * Mounted only for those beats, which is what keeps a fourth GL context off this page for the
   * rest of the loop.
   */
  const morphing =
    beat === 'generating' || beat === 'regenerating' || beat === 'resolving' || beat === 'apply';
  /** The hand-off beats: the band is still drawing, and the veil over it is on its way out. */
  const handingOff = beat === 'resolving' || beat === 'apply';

  /* The draft is plain until the brand note is answered; the rewrite is what brings the brand. */
  const look = applied ? 'brand' : 'plain';
  const ink = applied ? METRICS_INK : METRICS_PLAIN_INK;

  const headline: SlideHeadlineStyle = {
    text: applied ? APPLIED_HEADLINE : undefined,
    fontFamily: restyled ? AFTER.stack : BEFORE.stack,
    fontWeight: restyled ? AFTER.weight : BEFORE.weight,
    fontSize: restyled ? AFTER.size : BEFORE.size,
    color: ink,
  };

  /*
   * The ring's rect, measured rather than written down.
   *
   * `ArtifactFrame`'s `.canvas` is the only positioned ancestor between here and the headline, and
   * it is authored at natural size with the scale living in a CSS transform — so the headline's
   * `offsetLeft/Top/Width/Height` ARE artifact coordinates, with no scale to divide out. Measured
   * instead of hardcoded because the point of the beat is that the type grows: a written-down rect
   * would sit still while the thing it is ringing gets bigger.
   */
  const headlineRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState({ x: 84, y: 84, w: 640, h: 69 });

  useIsomorphicLayoutEffect(() => {
    const node = headlineRef.current;
    if (!node) return;
    const measure = () => {
      setRect((prev) => {
        const next = { x: node.offsetLeft, y: node.offsetTop, w: node.offsetWidth, h: node.offsetHeight };
        if (!next.w || !next.h) return prev;
        const same =
          Math.abs(prev.x - next.x) < 0.5 &&
          Math.abs(prev.y - next.y) < 0.5 &&
          Math.abs(prev.w - next.w) < 0.5 &&
          Math.abs(prev.h - next.h) < 0.5;
        return same ? prev : next;
      });
    };
    measure();
    // The restyle is a 320ms transition, so the box is a moving target for the length of it. One
    // observer catches every frame of the growth without a poll.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [restyled]);

  /*
   * THE NOTES, one beat at a time, and the bus emptied the moment the pen is up.
   *
   * A marker's number is its place in the bus, so the order notes are ADDED is the order they are
   * read. This used to be three effects, and the second note's was not gated on the pen: after the
   * rewrite cleared the bus it re-added the subtitle note on `apply`, into a bus nobody was drawing.
   * The next loop then found it waiting — the subtitle read 1, the headline read 2, and both markers
   * landed together on the first beat (Beka, 2026-09-12: put them chronologically). Clearing on the
   * first note's own beat, and adding the second only after the first, makes the order a fact of the
   * beats rather than of what the previous loop happened to leave behind.
   */
  const noteIds = useRef<{ one?: string; two?: string }>({});

  useEffect(() => {
    if (beat === 'annotate') {
      clearAnnotations(DEMO_SLIDE_KEY);
      // An AREA note — `w`/`h` present — which is the pen's drag-select, roped round the headline.
      // `rect` is read here rather than tracked: a drag-select is where the pen drew it.
      noteIds.current = {
        one: addAnnotation(DEMO_SLIDE_KEY, {
          x: rect.x - 16,
          y: rect.y - 16,
          w: rect.w + 32,
          h: rect.h + 32,
          text: NOTE_HEADLINE,
          element: 'Headline',
          elementPath: 'section > .metrics > h1',
        }).id,
      };
      return;
    }
    if (beat === 'second' && noteIds.current.one && !noteIds.current.two) {
      /*
       * Put the first note down before the second lands. `requestAnnotationEdit` LIGHTS the note it
       * opens, and closing the composer from out here (the remount) does not unlight it — so marker 1
       * sat hovered with no pointer on it: pencil for a number, its tooltip open, its region dashed in.
       */
      focusAnnotation(DEMO_SLIDE_KEY, null);
      noteIds.current.two = addAnnotation(DEMO_SLIDE_KEY, {
        ...STYLE_NOTE_POINT,
        text: NOTE_STYLE,
        element: 'Bar',
        elementPath: 'section > .metrics > .card:nth-child(2) > .bar',
      }).id;
      return;
    }
    if (!annotating && (noteIds.current.one || noteIds.current.two)) {
      focusAnnotation(DEMO_SLIDE_KEY, null);
      clearAnnotations(DEMO_SLIDE_KEY);
      noteIds.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat]);

  /*
   * Each note opens its composer on its own beat, after its marker has landed.
   *
   * `requestAnnotationEdit` and not `focusAnnotation`: focus only LIGHTS a marker; the edit request is
   * what puts the note back in the composer, which is the surface this row is here to show.
   */
  useEffect(() => {
    if (beat === 'note' && noteIds.current.one) requestAnnotationEdit(DEMO_SLIDE_KEY, noteIds.current.one);
    if (beat === 'secondNote' && noteIds.current.two) requestAnnotationEdit(DEMO_SLIDE_KEY, noteIds.current.two);
  }, [beat]);

  // The bus outlives this component, so the row takes its notes with it when it goes.
  useEffect(() => () => clearAnnotations(DEMO_SLIDE_KEY), []);

  /*
   * THE PRESENTED SLIDE'S RESTING POSE: the plate's size, and the width the editor drew the slide at.
   *
   * A pose rather than a layout, because the slide gets there by a transform from wherever the editor
   * had it — see the effect below, which is also what keeps `editorW` true to the pane's real fit.
   */
  const [plate, setPlate] = useState({ w: 796, h: 640 });
  const [editorW, setEditorW] = useState(EDITOR_ART_W);
  useIsomorphicLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (!w || !h) return;
      setPlate((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [hostRef]);
  const pose = presentPose(plate.w, plate.h, editorW);

  /*
   * THE HAND-OFF: the same slide, from where the editor drew it to the middle of the plate.
   *
   * The presented copy mounts already at its resting pose, and on the frame it mounts this measures
   * where the editor's artifact is on screen and plays a transform FROM there — before anything is
   * painted, so the copy's first visible frame is pixel-for-pixel the editor's slide, and the editor's
   * own artifact is hidden on that same frame. What the reader sees is one slide that lets go of its
   * editor and glides to the centre, the same size it was, while the chrome falls away behind it.
   *
   * Only on the way IN, and only from a beat that had the editor up. Off-screen and reduced-motion
   * readers get `useBeat`'s held last beat from the first render, so there is no editor to come from
   * and the slide is simply there.
   */
  const artRef = useRef<HTMLDivElement>(null);
  const presentedRef = useRef<HTMLDivElement>(null);
  const wasPresenting = useRef(presenting);
  useIsomorphicLayoutEffect(() => {
    const was = wasPresenting.current;
    wasPresenting.current = presenting;
    if (!presenting || was) return;
    const host = hostRef.current;
    const art = artRef.current;
    const node = presentedRef.current;
    if (!host || !art || !node) return;
    const hostBox = host.getBoundingClientRect();
    const artBox = art.getBoundingClientRect();
    if (!artBox.width) return;
    const from = artBox.width / NATURAL_W;
    const to = presentPose(host.clientWidth, host.clientHeight, artBox.width);
    // Keep the resting pose on the pane's real fit. A layout effect's update renders before paint,
    // so the copy's own style agrees with the animation's last frame when it hands back to it.
    if (Math.abs(artBox.width - editorW) > 0.5) setEditorW(artBox.width);
    node.animate(
      [
        { transform: `translate(${artBox.left - hostBox.left}px, ${artBox.top - hostBox.top}px) scale(${from})` },
        { transform: `translate(${to.x}px, ${to.y}px) scale(${to.s})` },
      ],
      { duration: PRESENT_MS, easing: PRESENT_EASE },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenting]);

  const toolbar = editing ? (
    <div className={styles.toolbarRise}>
      <TextToolbar
        theme={THEME}
        fontFamily={restyled ? AFTER.family : BEFORE.family}
        fontFamilies={[BEFORE.family, AFTER.family]}
        onFontFamily={() => {}}
        color={ink}
        onColor={() => {}}
        weight={restyled ? AFTER.weight : BEFORE.weight}
        onWeight={() => {}}
        size={restyled ? AFTER.size : BEFORE.size}
        onSize={() => {}}
        align="left"
        onAlign={() => {}}
        bold={false}
        onBold={() => {}}
        italic={false}
        onItalic={() => {}}
      />
    </div>
  ) : null;

  return (
    /*
     * The root is the PANEL, not the window.
     *
     * Every other row's root is its window, inset 60/60 and bleeding off two edges. This one needs
     * the whole plate for its finale — the presented slide centres in the plate, not in the part of
     * the window the plate shows — so the window becomes a child and the slide its sibling, both laid
     * out in the panel's own space.
     */
    <div ref={hostRef} className={cn(styles.root, className)} aria-hidden>
      <div className={cn(styles.window, presenting && styles.windowGone)}>
        <div className={styles.bar}>
          <DeckMark theme={THEME} hover="inert" />
          <span className={styles.barTitle}>Deck, Product v2 case</span>
        </div>

        <div className={styles.stage}>
          <PreviewPane
            theme={THEME}
            title="Where we started"
            meta="artifact 02"
            status="ready"
            kind="slide"
            naturalWidth={NATURAL_W}
            naturalHeight={NATURAL_H}
            indicator="2 of 8"
            showZoom={false}
            aboveArtifact={toolbar}
          >
            {/*
              All four sit in `ArtifactFrame`'s canvas, which is authored at 1280x720 with the scale in
              a CSS transform — so the ring's rect, the layer's coordinates and the veil's box are all
              plain artifact units, with no scale for anyone here to divide out.
            */}
            <>
              {/* The box the hand-off measures. Hidden, not removed, on the frame the copy takes over:
                  a removed artifact would re-fit the pane under a slide that is still leaving it. */}
              <div ref={artRef} className={cn(styles.art, presenting && styles.artHidden)}>
                <SlideArt kind="metrics" look={look} headline={headline} headlineRef={headlineRef} />
              </div>
              {editing ? (
                <TextEditRing rect={rect} label={restyled ? `${rect.w | 0} × ${rect.h | 0}` : 'editing'} />
              ) : null}
              {/*
                The pen, with the pointer taken off it. `active` is what makes the layer draw at all,
                and it is also what arms its crosshair and click-to-place — neither of which belongs
                on a marketing page, where the reader's click means "read on". Notes come off the bus.
              */}
              {annotating ? (
                <div className={styles.annotateLock}>
                  <AnnotateLayer
                    /*
                     * KEYED ON WHICH NOTE IS BEING ASKED, and it has to be.
                     *
                     * `NotePopup` seeds its textarea from `initialValue` on MOUNT. Handing the same
                     * popup a second note swaps the header and leaves the first note's words in the
                     * box — so the second ask would read as the first. A remount is also the only way
                     * to close a composer from out here: `editing` is the layer's own state, cleared
                     * by its Save and Cancel. The notes live on the bus and survive it.
                     *
                     * The key turns on `second`, the beat the second MARKER lands — not on
                     * `secondNote`, the beat its composer opens. Turned there, the first composer was
                     * still up when the second marker arrived, and the two notes overlapped for a
                     * beat. Now each is one clean step: note, composer, closed; note, composer.
                     */
                    key={at('second') ? 'note-two' : 'note-one'}
                    slideId={DEMO_SLIDE_KEY}
                    width={NATURAL_W}
                    height={NATURAL_H}
                    active
                    theme={THEME}
                  />
                </div>
              ) : null}
              {/*
                The veil, ABOVE everything drawn on the artifact.
                `AnnotateLayer` carries `z-index: 10`, so a generation overlay without one of its own
                paints under the markers — saying the notes survived the rewrite they caused.
              */}
              {morphing ? (
                <div className={cn(styles.veil, handingOff && styles.veilGone)}>
                  <MorphCanvas width={NATURAL_W} height={NATURAL_H} phase="band" />
                </div>
              ) : null}
            </>
          </PreviewPane>
        </div>
      </div>

      {/* The finale: the edited slide, on its own, in the middle of the plate. */}
      {presenting ? (
        <div
          ref={presentedRef}
          className={styles.presented}
          style={
            {
              '--present-x': `${pose.x}px`,
              '--present-y': `${pose.y}px`,
              '--present-s': pose.s,
              '--present-radius': `${FRAME_RADIUS / pose.s}px`,
            } as CSSProperties
          }
        >
          <SlideArt kind="metrics" look="brand" headline={headline} />
        </div>
      ) : null}
    </div>
  );
}

EditAndPresentDemo.displayName = 'EditAndPresentDemo';
